// src/components/DashCalculator.jsx
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './dash.css';
import './dash-pdf.css';

const MEALS = [
  'BREAKFAST',
  'MORNING SNACK',
  'LUNCH',
  'AFTERNOON SNACK',
  'DINNER',
];

const LOGO_URL =
  'https://onemed.clinic/wp-content/uploads/2023/11/17009d89d69bdae16833d13d2c812ea68dda52ad.png';

// Excel path from HTML parameter
const EXCEL_PATH =
  typeof document !== 'undefined'
    ? document.getElementById('dash-root')?.dataset?.excel || '/calculator.xlsx'
    : '/calculator.xlsx';

const getGroupLabel = (groupText) => {
  const textLower = groupText.toLowerCase();
  if (textLower.includes('protein')) {
    return 'Protein';
  }
  if (textLower.includes('meats')) {
    return 'Protein';
  }
  return groupText;
};

// -----------------------
// MEAL SECTION COMPONENT
// -----------------------
const MealSection = ({
  meal,
  groupedData,
  items,
  onTryAdd,
  onRemoveItem,
  error,
  exporting,
}) => {
  const [group, setGroup] = useState('');
  const [food, setFood] = useState('');
  const [servingSize, setServingSize] = useState('');

  const groups = Object.keys(groupedData);

  const foodsForGroup = useMemo(() => {
    if (!group) return [];
    return Object.keys(groupedData[group] || {});
  }, [group, groupedData]);

  const servingSizeOptions = useMemo(() => {
    if (!group || !food) return [];
    return groupedData[group][food]?.sizes || [];
  }, [group, food, groupedData]);

  const resetSelectors = () => {
    setGroup('');
    setFood('');
    setServingSize('');
  };

  const mealItems = items.filter((i) => i.meal === meal);
  console.log('I was triggered during render');
  // const sort = foodsForGroup.sort((a, b) => (a > b ? -1 : 1));
  // console.log(sort);
  const capitalizeWords = (text = '') =>
    String(text)
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  return (
    <div class='dash-card dash-meal-card'>
      <h3 class='dash-meal-title'>{meal}</h3>

      <div class={`dash-row ${exporting ? 'hidden-export' : ''}`}>
        {/* GROUP */}
        <label>
          Food Group
          <select
            value={group}
            onChange={(e) => {
              setGroup(e.target.value);
              setFood('');
              setServingSize('');
            }}
          >
            <option value=''>Select group...</option>
            {groups.map((g) => (
              <option key={g} value={g}>
                {capitalizeWords(getGroupLabel(g))}
              </option>
            ))}
          </select>
        </label>

        {/* FOOD */}
        <label>
          Food
          <select
            value={food}
            onChange={(e) => {
              setFood(e.target.value);
              setServingSize('');
            }}
            disabled={!group}
          >
            <option value=''>
              {group ? 'Select food...' : 'Select a group first...'}
            </option>
            {foodsForGroup
              .sort((a, b) => a.localeCompare(b))
              .map((f) => (
                <option key={f} value={f}>
                  {capitalizeWords(f)}
                </option>
              ))}
          </select>
        </label>

        {/* SERVINGS SIZE */}
        <label>
          Servings size
          <select
            value={servingSize}
            onChange={(e) => setServingSize(e.target.value)}
            disabled={!food}
          >
            <option value=''>
              {food ? 'Select serving size...' : 'Select a food first...'}
            </option>
            {servingSizeOptions.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        {!exporting && (
          <button
            class='dash-add-btn'
            onClick={() =>
              onTryAdd({
                meal,
                group,
                food,
                servingSize,
                reset: resetSelectors,
              })
            }
          >
            Add
          </button>
        )}
      </div>

      {error && <div class='dash-error'>{error}</div>}

      {/* ADDED ITEMS */}
      {mealItems.length > 0 && (
        <div class='dash-meal-list'>
          {mealItems.map((i) => (
            <div key={i.id} class='dash-list-item'>
              <div>
                <strong>{i.food}</strong>{' '}
                <span class='dash-size'>{i.servingSize}</span>
                <span class='dash-pill'>{getGroupLabel(i.group)}</span>
              </div>

              {!exporting && (
                <button
                  class='dash-remove-btn'
                  onClick={() => onRemoveItem(i.id)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -----------------------
// MAIN COMPONENT
// -----------------------
const DashCalculator = () => {
  const [rawData, setRawData] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [items, setItems] = useState([]);
  const [errorByMeal, setErrorByMeal] = useState({});
  const [exporting, setExporting] = useState(false);

  // -----------------------
  // LOAD EXCEL
  // -----------------------
  useEffect(() => {
    const loadExcel = async () => {
      try {
        const res = await fetch(EXCEL_PATH);
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets['DASH'] || wb.Sheets[wb.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const cleaned = rows
          .map((r) => ({
            group: r['Froud Group'],
            food: r['Food'],
            servingSize: r['Servings size'],
            servingValue: r['SERVINGS per day Food'],
            limit: Number(r['SERVINGS PER DAY']) || 0,
          }))
          .filter((r) => r.group && r.food);

        setRawData(cleaned);
      } catch (e) {
        setLoadError('Error loading Excel file');
      } finally {
        setLoading(false);
      }
    };

    loadExcel();
  }, []);

  // -----------------------
  // GROUP BY Food
  // -----------------------
  useEffect(() => {
    const map = {};

    rawData.forEach((row) => {
      if (!map[row.group]) map[row.group] = {};
      if (!map[row.group][row.food]) {
        map[row.group][row.food] = {
          sizes: [],
          servingValuesBySize: {},
          limit: row.limit,
        };
      }

      // Add Servings size
      if (!map[row.group][row.food].sizes.includes(row.servingSize)) {
        map[row.group][row.food].sizes.push(row.servingSize);
      }

      // FIRST SERVINGS per day Food for each size
      if (!map[row.group][row.food].servingValuesBySize[row.servingSize]) {
        map[row.group][row.food].servingValuesBySize[row.servingSize] =
          row.servingValue;
      }
    });

    // Keep original order from Excel
    setGroupedData(map);
  }, [rawData]);

  const limits = useMemo(() => {
    const out = {};
    rawData.forEach((r) => {
      if (!out[r.group] || r.limit > out[r.group]) {
        out[r.group] = r.limit;
      }
    });
    return out;
  }, [rawData]);

  const totals = useMemo(() => {
    const t = {};
    items.forEach((i) => {
      t[i.group] = (t[i.group] || 0) + Number(i.value);
    });
    return t;
  }, [items]);

  const setMealError = (meal, msg) =>
    setErrorByMeal((prev) => ({ ...prev, [meal]: msg }));

  // -----------------------
  // ADD ITEM
  // -----------------------
  const handleTryAdd = ({ meal, group, food, servingSize, reset }) => {
    if (!group || !food || !servingSize) {
      setMealError(meal, 'Please select group, food and serving size.');
      return;
    }

    const value = groupedData[group][food].servingValuesBySize[servingSize];

    const used = totals[group] || 0;
    const limit = limits[group];

    if (used + Number(value) > limit) {
      setMealError(
        meal,
        `Daily limit exceeded for "${group}". Limit: ${limit}, used: ${used}.`,
      );
      return;
    }

    setItems((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        meal,
        group,
        food,
        servingSize,
        value: Number(value),
      },
    ]);

    setMealError(meal, '');
    reset();
  };

  const handleRemove = (id) =>
    setItems((prev) => prev.filter((i) => i.id !== id));

  // -----------------------
  // EXPORT PDF
  // -----------------------
  const exportPDF = async () => {
    const pdfElement = document.getElementById('pdf-layout');

    await new Promise((r) => setTimeout(r, 200)); // Wait for DOM updates

    const canvas = await html2canvas(pdfElement, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
    });

    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // Raw image size (pixels → mm conversion handled by jsPDF later)
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;

    let finalW = imgW;
    let finalH = imgH;

    // AUTO-FIT if taller than 1 page
    if (imgH > pageH) {
      const scale = pageH / imgH;
      finalW = imgW * scale;
      finalH = imgH * scale;
    }

    // CENTERING LOGIC (horizontal + vertical)
    const x = (pageW - finalW) / 2; // center horizontally
    const y = (pageH - finalH) / 2; // center vertically

    pdf.addImage(imgData, 'PNG', x, y, finalW, finalH);
    pdf.save('dash-diet-meal-planner.pdf');
  };

  if (loading) return <p>Loading DASH data…</p>;
  if (loadError) return <p style={{ color: 'red' }}>{loadError}</p>;

  const groups = Object.keys(groupedData);

  return (
    <div
      id='dash-root'
      class={`dash-container ${exporting ? 'exporting' : ''}`}
    >
      <div class='dash-left'>
        {MEALS.map((meal) => (
          <MealSection
            key={meal}
            meal={meal}
            groupedData={groupedData}
            items={items}
            onTryAdd={handleTryAdd}
            onRemoveItem={handleRemove}
            error={errorByMeal[meal] || ''}
            exporting={exporting}
          />
        ))}
      </div>

      {/* SUMMARY */}
      <div class='dash-right'>
        <h3 class='dash-card-title'>SERVINGS PER DAY</h3>

        <div class='dash-summary-card'>
          <table class='dash-table'>
            <thead>
              <tr>
                <th>Food Group</th>
                <th>Allowed</th>
                <th>Used</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr
                  key={g}
                  class={
                    (totals[g] || 0) > limits[g]
                      ? 'dash-row-exceeded'
                      : (totals[g] || 0) === limits[g]
                        ? 'dash-row-full'
                        : ''
                  }
                >
                  <td>{getGroupLabel(g)}</td>
                  <td>{limits[g]}</td>
                  <td>{totals[g] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p class='dash-hint'>
            <span class='color-green'>GREEN: Daily limit fulfilled.</span>
          </p>
          <p class='dash-hint'>
            Disclaimer: The information provided on this website is for general
            informational purposes only and does not constitute medical advice.
          </p>

          {/* Export button BELOW the text */}
          {!exporting && (
            <button class='dash-export-btn' onClick={exportPDF}>
              Export to PDF
            </button>
          )}
        </div>
      </div>
      {/* ----------------------- PDF LAYOUT ----------------------- */}

      <div id='pdf-layout' className='pdf-render'>
        <div className='pdf-page'>
          {/* HEADER */}
          <div className='pdf-header'>
            <img src={LOGO_URL} className='pdf-logo' alt='Logo' />

            <div className='pdf-title-block'>
              <h1>DASH DIET MEAL PLANNER</h1>
              <h2>FOR 1,800 TO 2,000 CALORIES PER DAY</h2>
              <a
                className='pdf-link'
                href='https://onemed.clinic/free-dash-diet-meal-plan'
              >
                HTTPS://ONEMED.CLINIC/FREE-DASH-DIET-MEAL-PLAN
              </a>
            </div>
          </div>

          {/* BODY */}
          <div className='pdf-body'>
            {/* LEFT COLUMN */}
            <div className='pdf-left'>
              {MEALS.map((meal) => (
                <div className='pdf-meal-card' key={meal}>
                  <h3>{meal}</h3>

                  {items
                    .filter((i) => i.meal === meal)
                    .map((i) => (
                      <div className='pdf-food-row' key={i.id}>
                        <span>{i.food.trim()}:</span>
                        <span>{i.servingSize}</span>|
                        <span>{getGroupLabel(i.group)}</span>
                      </div>
                    ))}
                </div>
              ))}
            </div>

            {/* RIGHT COLUMN */}
            <div className='pdf-right'>
              <h3 className='pdf-servings-title'>SERVINGS PER DAY</h3>

              <table className='pdf-table'>
                <thead>
                  <tr>
                    <th>Food Group</th>
                    <th>Allowed</th>
                    <th>Used</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr
                      key={g}
                      class={
                        (totals[g] || 0) > limits[g]
                          ? 'dash-row-exceeded'
                          : (totals[g] || 0) === limits[g]
                            ? 'dash-row-full'
                            : ''
                      }
                    >
                      <td>{getGroupLabel(g.trim())}</td>
                      <td>{limits[g]}</td>
                      <td>{totals[g] || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className='pdf-note'>
                <span class='color-green'>GREEN: Daily limit fulfilled.</span>
              </p>

              <p className='pdf-legal'>
                Disclaimer: The information provided on this website is for
                general informational purposes only and does not constitute
                medical advice.
              </p>
            </div>
          </div>

          {/* FOOTER */}
          <div className='pdf-footer'>
            LEARN MORE ABOUT THE DASH DIET:&nbsp;
            <a href='https://onemed.clinic/dash-diet'>
              HTTPS://ONEMED.CLINIC/DASH-DIET
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashCalculator;
